import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, MapPin, Menu, X } from 'lucide-react';
import { API } from '../utils/api';

const DEFAULT_HOURS_SUMMARY = "Mar-Ven 9h-18h30 • Sam 9h-17h30";

const formatHoursForHeader = (hours) => {
  if (!hours || !hours.length) return DEFAULT_HOURS_SUMMARY;
  const abbrMap = {
    "Lundi": "Lun", "Mardi": "Mar", "Mercredi": "Mer",
    "Jeudi": "Jeu", "Vendredi": "Ven", "Samedi": "Sam", "Dimanche": "Dim"
  };
  const fmt = (t) => t ? t.replace(':30', 'h30').replace(':00', 'h') : '';
  const groups = [];
  let currentGroup = null;
  hours.forEach(h => {
    if (!h.isOpen) { currentGroup = null; return; }
    const timeKey = `${h.open}|${h.close}|${h.hasBreak ? `${h.breakStart}-${h.breakEnd}` : ''}`;
    if (currentGroup && currentGroup.timeKey === timeKey) {
      currentGroup.days.push(abbrMap[h.day] || h.day.substring(0, 3));
    } else {
      currentGroup = { timeKey, days: [abbrMap[h.day] || h.day.substring(0, 3)], open: h.open, close: h.close, hasBreak: h.hasBreak || false, breakStart: h.breakStart || '', breakEnd: h.breakEnd || '' };
      groups.push(currentGroup);
    }
  });
  if (!groups.length) return "Fermé";
  return groups.map(g => {
    const ds = g.days.length > 1 ? `${g.days[0]}-${g.days[g.days.length - 1]}` : g.days[0];
    const timeStr = g.hasBreak && g.breakStart && g.breakEnd
      ? `${fmt(g.open)}-${fmt(g.breakStart)} / ${fmt(g.breakEnd)}-${fmt(g.close)}`
      : `${fmt(g.open)}-${fmt(g.close)}`;
    return `${ds} ${timeStr}`;
  }).join(' • ');
};

const computeOpenStatus = (hours) => {
  if (!hours || !hours.length) return { isOpen: null, detail: '' };
  const now = new Date();
  const dayNames = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  const todayName = dayNames[now.getDay()];
  const todayHours = hours.find(h => h.day === todayName);
  const fmt = (t) => t ? t.replace(':30', 'h30').replace(':00', 'h') : '';

  const findNextOpen = (fromDay) => {
    const order = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
    const idx = order.indexOf(fromDay);
    for (let i = 1; i <= 7; i++) {
      const next = order[(idx + i) % 7];
      const d = hours.find(h => h.day === next);
      if (d && d.isOpen && d.open) {
        return `Ouvre ${i === 1 ? 'demain' : next.toLowerCase()} à ${fmt(d.open)}`;
      }
    }
    return '';
  };

  if (!todayHours || !todayHours.isOpen || !todayHours.open || !todayHours.close) {
    return { isOpen: false, detail: findNextOpen(todayName) };
  }

  const [openH, openM] = todayHours.open.split(':').map(Number);
  const [closeH, closeM] = todayHours.close.split(':').map(Number);
  const cur = now.getHours() * 60 + now.getMinutes();
  const openMin = openH * 60 + openM;
  const closeMin = closeH * 60 + closeM;

  if (cur >= openMin && cur < closeMin) {
    // Vérifier si pendant la coupure
    if (todayHours.hasBreak && todayHours.breakStart && todayHours.breakEnd) {
      const [bs, bsm] = todayHours.breakStart.split(':').map(Number);
      const [be, bem] = todayHours.breakEnd.split(':').map(Number);
      const breakStartMin = bs * 60 + bsm;
      const breakEndMin = be * 60 + bem;
      if (cur >= breakStartMin && cur < breakEndMin) {
        return { isOpen: false, detail: `Ouvre à ${fmt(todayHours.breakEnd)}` };
      }
      // Ouvert — préciser si avant ou après coupure
      if (cur < breakStartMin) {
        return { isOpen: true, detail: `Ferme à ${fmt(todayHours.breakStart)}` };
      }
    }
    return { isOpen: true, detail: `Ferme à ${fmt(todayHours.close)}` };
  } else if (cur < openMin) {
    return { isOpen: false, detail: `Ouvre à ${fmt(todayHours.open)}` };
  } else {
    return { isOpen: false, detail: findNextOpen(todayName) };
  }
};

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoursSummary, setHoursSummary] = useState(DEFAULT_HOURS_SUMMARY);
  const [openStatus, setOpenStatus] = useState({ isOpen: null, detail: '' });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API}/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.hours) {
            setHoursSummary(formatHoursForHeader(data.hours));
            setOpenStatus(computeOpenStatus(data.hours));
          }
        }
      } catch {
        // Keep fallback default
      }
    };
    fetchSettings();
    // Refresh open status every minute
    const interval = setInterval(() => {
      fetchSettings();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleCall = () => {
    window.location.href = 'tel:+33978802623';
  };

  const handleDirections = () => {
    window.open('https://www.google.com/maps/search/?api=1&query=1+Place+du+18+Novembre+1944,+90600+Grandvillars', '_blank');
  };

  return (
    <>
      <header className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white shadow-lg'
          : 'bg-gradient-to-b from-white via-white to-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 relative">
          {/* Top bar - Info & Contact */}
          <div className={`flex items-center justify-between text-xs border-b transition-all duration-500 overflow-hidden ${
            isScrolled
              ? 'h-0 py-0 opacity-0 border-transparent'
              : 'h-auto py-1.5 opacity-100 border-gray-200'
          }`}>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-3 h-3 text-green-600" />
                <span className="text-xs">1 Place du 18 Novembre 1944, Grandvillars</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <span className={`inline-block w-1.5 h-1.5 rounded-full animate-pulse ${
                  openStatus.isOpen === false ? 'bg-red-500' : 'bg-green-500'
                }`}></span>
                <span data-testid="open-status-badge" className={`font-semibold text-xs ${
                  openStatus.isOpen === false ? 'text-red-600' : 'text-green-700'
                }`}>
                  {openStatus.isOpen === null ? 'Ouvert' : openStatus.isOpen ? 'Ouvert' : 'Fermé'}
                </span>
                {openStatus.detail && (
                  <span className="text-gray-400 text-xs hidden sm:inline">• {openStatus.detail}</span>
                )}
                {!openStatus.detail && (
                  <span className="text-gray-500 text-xs">{hoursSummary}</span>
                )}
              </div>
              <button
                onClick={handleCall}
                className="flex items-center gap-1.5 px-3 py-1 bg-red-600 text-white rounded-full hover:bg-red-700 text-xs font-medium transition-all hover:scale-105"
              >
                <Phone className="w-3 h-3" />
                <span>+33 9 78 80 26 23</span>
              </button>
            </div>
          </div>

          {/* Main Header */}
          <div className={`flex flex-col items-center justify-center transition-all duration-500 ${
            isScrolled ? 'py-2' : 'py-3'
          }`}>
            {/* Logo Section */}
            <Link to="/" className="flex items-center gap-3 group mb-4">
              <div className={`relative rounded-full overflow-hidden border-2 border-green-700 shadow-sm transition-all duration-500 ${
                isScrolled ? 'w-10 h-10' : 'w-12 h-12'
              }`}>
                <img
                  alt="Logo d'IL TRULLO"
                  className="w-full h-full object-cover"
                  src="https://customer-assets.emergentagent.com/job_epicerie-copy/artifacts/v7cxe0l9_LE%20LOGO%20DE%20FACEBOOK.png"
                />
              </div>
              <div>
                <h1 className={`font-bold text-gray-900 transition-all duration-500 ${
                  isScrolled ? 'text-lg md:text-xl' : 'text-xl md:text-2xl'
                }`} style={{ fontFamily: 'Libre Baskerville, serif' }}>
                  IL TRULLO
                </h1>
                <p className={`text-xs italic text-gray-600 hidden md:block transition-all duration-500 ${
                  isScrolled ? 'opacity-0 h-0' : 'opacity-100 h-auto'
                }`} style={{ fontFamily: 'Libre Baskerville, serif' }}>
                  Épicerie fine italienne
                </p>
              </div>
            </Link>

            {/* Navigation Desktop */}
            <nav className={`hidden lg:flex items-center justify-center transition-all duration-300 ${
              isScrolled ? 'gap-4' : 'gap-6'
            }`}>
              <Link
                to="/"
                className={`font-medium transition-all duration-300 relative ${
                  isScrolled ? 'text-xs' : 'text-sm'
                } ${
                  isActive('/') ? 'text-green-700' : 'text-gray-700 hover:text-green-700'
                }`}
              >
                Accueil
                {isActive('/') && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-green-700"></span>}
              </Link>
              <Link
                to="/infos-pratiques"
                className={`font-medium transition-all duration-300 relative whitespace-nowrap ${
                  isScrolled ? 'text-xs' : 'text-sm'
                } ${
                  isActive('/infos-pratiques') ? 'text-green-700' : 'text-gray-700 hover:text-green-700'
                }`}
              >
                Infos pratiques
                {isActive('/infos-pratiques') && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-green-700"></span>}
              </Link>
            </nav>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden absolute top-4 right-6 p-2 text-gray-700 hover:text-green-700 transition-colors z-10"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <nav className="container mx-auto py-4 flex flex-col gap-2">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`font-medium transition-colors px-4 py-2 rounded ${
                  isActive('/') ? 'text-green-700 bg-green-50' : 'text-gray-700 hover:text-green-700 hover:bg-gray-50'
                }`}
              >
                Accueil
              </Link>
              <Link
                to="/infos-pratiques"
                onClick={() => setMobileMenuOpen(false)}
                className={`font-medium transition-colors px-4 py-2 rounded ${
                  isActive('/infos-pratiques') ? 'text-green-700 bg-green-50' : 'text-gray-700 hover:text-green-700 hover:bg-gray-50'
                }`}
              >
                Infos pratiques
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
